<template>
  <div class="ds-result-headline padding-0-mobile col">
    <span
      v-if="shouldShowHvdFoundCountTooltip"
      class="search-count-tooltip-trigger"
      @mouseenter="showFloatingCountTooltip($event, hvdSelectionCountTooltip)"
      @mouseleave="hideFloatingCountTooltip"
      @focus="showFloatingCountTooltip($event, hvdSelectionCountTooltip)"
      @blur="hideFloatingCountTooltip"
      tabindex="0"
    >
      {{ displayCountText }}
    </span>
    <span v-else>{{ displayCountText }}</span>
  </div>
</template>

<script>


export default {
  name: "SearchCount",
  data() {
    return {
      floatingCountTooltipEl: null,
      floatingCountTooltipAnchorEl: null,
    };
  },
  props: {
    config: Object
  },
  computed: {
    getCount() {
      return this.$store.getters[`${this.config.getCount[0]}/${this.config.getCount[1]}`];
    },
    getLoading() {
      return this.$store.getters[`${this.config.getLoading[0]}/${this.config.getLoading[1]}`];
    },
    getSortType() {
      return this.$store.getters['search/getSortType'];
    },
    getMostLikedTotal() {
      return this.$store.getters['search/getMostLikedTotal'];
    },
    getLoadedDatasets() {
      return this.$store.getters['datasets/getDatasets'] || [];
    },
    loadedDatasetsCount() {
      return Array.isArray(this.getLoadedDatasets)
        ? this.getLoadedDatasets.length
        : 0;
    },
    hvdSelectionCountTooltip() {
      return 'This count shows the number of datasets explicitly assigned to this High-Value Dataset category. The total "Datasets found" may be different because datasets can belong to multiple selected sub-categories, and the search results count each dataset only once.';
    },
    isHvdCategoryTreeActive() {
      const hvdSelection = this.$route.query?.hvdCategory;
      if (Array.isArray(hvdSelection)) return hvdSelection.length > 0;
      return hvdSelection != null && String(hvdSelection).length > 0;
    },
    usesFoundCountLabel() {
      const supportedCountKeys = [
        "message.datasets.countMessage",
        "message.search.countMessage.combined",
      ];
      return supportedCountKeys.includes(this.config?.textKeys?.count);
    },
    shouldShowHvdFoundCountTooltip() {
      return !this.getLoading && this.isHvdCategoryTreeActive && this.usesFoundCountLabel;
    },
    displayCountText() {
      if (this.getLoading) return this.$t(this.config.textKeys.loading);
      const configuredCount =
        this.getSortType === 'mostLiked'
          ? this.getMostLikedTotal
          : this.getCount;
      const countValue =
        configuredCount === undefined || configuredCount === null
          ? this.loadedDatasetsCount
          : configuredCount;
      return `${this.$t(this.config.textKeys.count)} (${countValue.toLocaleString("fi")})`;
    }
  },
  methods: {
    updateFloatingCountTooltipPosition() {
      if (!this.floatingCountTooltipEl || !this.floatingCountTooltipAnchorEl) return;

      const anchorRect = this.floatingCountTooltipAnchorEl.getBoundingClientRect();
      const tooltipRect = this.floatingCountTooltipEl.getBoundingClientRect();
      const viewportPadding = 8;

      let left = anchorRect.right - tooltipRect.width;
      left = Math.max(
        viewportPadding,
        Math.min(left, window.innerWidth - tooltipRect.width - viewportPadding)
      );

      let top = anchorRect.top - tooltipRect.height - 8;
      if (top < viewportPadding) {
        top = anchorRect.bottom + 8;
      }

      this.floatingCountTooltipEl.style.left = `${left}px`;
      this.floatingCountTooltipEl.style.top = `${top}px`;
    },
    showFloatingCountTooltip(event, text) {
      if (!text) return;

      this.hideFloatingCountTooltip();

      const tooltipEl = document.createElement("div");
      tooltipEl.className = "search-count-tooltip-floating";
      tooltipEl.textContent = text;
      document.body.appendChild(tooltipEl);

      this.floatingCountTooltipEl = tooltipEl;
      this.floatingCountTooltipAnchorEl = event.currentTarget;

      this.updateFloatingCountTooltipPosition();

      window.addEventListener("scroll", this.updateFloatingCountTooltipPosition, true);
      window.addEventListener("resize", this.updateFloatingCountTooltipPosition);
    },
    hideFloatingCountTooltip() {
      window.removeEventListener("scroll", this.updateFloatingCountTooltipPosition, true);
      window.removeEventListener("resize", this.updateFloatingCountTooltipPosition);

      if (this.floatingCountTooltipEl && this.floatingCountTooltipEl.parentNode) {
        this.floatingCountTooltipEl.parentNode.removeChild(this.floatingCountTooltipEl);
      }

      this.floatingCountTooltipEl = null;
      this.floatingCountTooltipAnchorEl = null;
    },
  },
  beforeUnmount() {
    this.hideFloatingCountTooltip();
  }
}
</script>

<style lang="scss" scoped>
.search-count-tooltip-trigger {
  cursor: help;
}

:global(.search-count-tooltip-floating) {
  position: fixed;
  width: 360px;
  max-width: min(360px, calc(100vw - 16px));
  background: #000;
  color: #fff;
  font-size: 14px;
  line-height: 1.4;
  padding: 4px 8px;
  border-radius: 4px;
  text-align: left;
  pointer-events: none;
  z-index: 20000;
}

@media screen and (max-width: 767.5px) {
  .ds-result-headline {
    margin-left: 0;
    margin-top: 20px;
  }
  .padding-0-mobile {
    padding: 0;
  }
}
</style>
